import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import styled from "styled-components"
import { Input as BaseInput, type InputProps } from "./Input"

const Input = styled(BaseInput)``

export interface NumberInputProps
  extends Omit<
    InputProps,
    | "value"
    | "maxLength"
    | "inputMode"
    | "placeholder"
    | "type"
    | "autoCompleteSource"
    | "resetButton"
    | "onChange"
  > {
  value: number

  min?: number
  max?: number
  /** Amount of change on mouse wheel or arrow keys. Default 1 */
  step?: number
  /** Don't accept decimals */
  noDecimals?: boolean
  /** Whether it should be possible to clear the input */
  clearable?: boolean

  /** Fires when input is cleared if clearable == true */
  onCleared?(): void
  onChange(value: number, event?: React.ChangeEvent<HTMLInputElement>): void
}

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,

      min = Number.NEGATIVE_INFINITY,
      max = Number.POSITIVE_INFINITY,
      step = 1,
      noDecimals = false,
      clearable = false,

      onCleared,
      onChange,

      ...inputProps
    },
    ref,
  ) => {
    const selectTarget = useRef<HTMLInputElement | null>(null)

    const [inputValue, setInputValue] = useState(value + "")
    const [doSelect, setDoSelect] = useState(false)

    const validateNumber = (value: string) => !Number.isNaN(Number(value))

    function scrollValue(decrease?: boolean) {
      if (inputProps.disabled) return

      if (decrease) {
        if (value - step < min) return
        onChange(value - step)
      } else {
        if (value + step > max) return
        onChange(value + step)
      }
    }

    function handleChange(
      val: string,
      evt?: React.ChangeEvent<HTMLInputElement>,
    ): void {
      if (noDecimals) val = val.replace(".", "")

      if (val === "") {
        const newVal = min === Number.NEGATIVE_INFINITY ? 0 : min
        onChange(newVal, evt)

        if (clearable) {
          onCleared?.()
          setInputValue("")
          return
        }

        selectTarget.current = evt?.target ?? null
        setDoSelect(true)

        setInputValue(newVal + "")
        return
      }

      if (val === "-" || val === "-0") {
        if (min >= 0) return handleChange("", evt)
        setInputValue(val)
        onChange(0, evt)
        return
      }

      if (!validateNumber(val)) return
      setInputValue(val)

      const number = Number(val)
      onChange(number, evt)
    }
    function handleBlur(evt: React.FocusEvent<HTMLInputElement>) {
      if (value < min) {
        handleChange(min + "", evt)
        return
      }
      if (value > max) {
        handleChange(max + "", evt)
        return
      }
      if (inputValue === "-") {
        handleChange("0", evt)
        return
      }

      inputProps.onBlur?.(evt)
    }
    function handleWheel(evt: React.WheelEvent<HTMLInputElement>) {
      inputProps.onWheel?.(evt)
      if (evt.deltaY === 0 || evt.defaultPrevented) return

      scrollValue(evt.deltaY > 0)
    }
    function handleKeyDown(evt: React.KeyboardEvent<HTMLInputElement>) {
      inputProps.onKeyDown?.(evt)
      if (evt.defaultPrevented) return

      if (evt.key === "ArrowDown") scrollValue(true)
      else if (evt.key === "ArrowUp") scrollValue(false)
      else return

      evt.preventDefault()
    }

    useEffect(() => {
      if (clearable && !inputValue) return
      if (
        inputValue === "-" ||
        /\.0*$/.test(inputValue) ||
        /\.\d+0+$/.test(inputValue)
      )
        return
      if (value + "" !== inputValue) setInputValue(value + "")
    }, [value, inputValue, clearable])

    useLayoutEffect(() => {
      if (doSelect && !!selectTarget.current) {
        selectTarget.current.select()
        setDoSelect(false)
        selectTarget.current = null
      }
    }, [value, doSelect])

    return (
      <Input
        ref={ref}
        value={inputValue}
        onChange={handleChange}
        {...inputProps}
        onBlur={handleBlur}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
      />
    )
  },
)
